// src/encounter/services/use-wrap-up.ts
// Encounter Wrap-Up record (PROD-1405 — follow-up scheduler).
//   GET  /api/Patients/GetWrapUpByPatientId?patientId=&encounterId=  (404 → null)
//   POST /api/Patients/CreateWrapUp      (body = WrapUpModel)        — no record yet
//   PUT  /api/Patients/UpdateWrapUp?id=  (body = WrapUpModel)        — existing record
// One wrap-up record per encounter, so `useSaveWrapUp` upserts: PUT when the body carries an id,
// else POST. The backend turns a non-zero followupCount into a follow-up appointment on sign/lock.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, put, ApiError } from '@/shared/api/api-client';
import type { WrapUpModel } from '@/types/wrap-up-api';
import { useNoteWrite } from './note-session';

export function wrapUpKey(
  encounterId: string | null | undefined,
  patientId: string | null | undefined,
) {
  return ['wrap-up', encounterId, patientId] as const;
}

/** Read the encounter's wrap-up record (404 → null). Returns the single record (the GET may
 *  return a bare object or a one-element array — handle both, like the sign-lock read). */
export function useWrapUp(
  encounterId: string | null | undefined,
  patientId: string | null | undefined,
) {
  return useQuery<WrapUpModel | null>({
    queryKey: wrapUpKey(encounterId, patientId),
    enabled: !!encounterId && !!patientId,
    queryFn: async ({ signal }) => {
      if (!encounterId || !patientId) return null;
      try {
        const res = await get<WrapUpModel | WrapUpModel[] | null>(
          `/api/Patients/GetWrapUpByPatientId?patientId=${encodeURIComponent(
            patientId,
          )}&encounterId=${encodeURIComponent(encounterId)}`,
          { signal },
        );
        if (Array.isArray(res)) return res[0] ?? null;
        return res ?? null;
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
  });
}

/** Upsert the wrap-up record: PUT when the body carries an `id`, else POST create. Refreshes the read.
 *  The `id` only ever comes from `useWrapUp` — which is ENCOUNTER-SCOPED (the GET filters by
 *  `encounterId` and the query key includes it), and the caller resets its follow-up state on
 *  encounter change (followUpFromWrapUp). So the PUT can't target a prior encounter's record. */
export function useSaveWrapUp(
  encounterId: string | null | undefined,
  patientId: string | null | undefined,
) {
  const noteWrite = useNoteWrite();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: WrapUpModel) =>
      noteWrite(() =>
        body.id
          ? put<string>(`/api/Patients/UpdateWrapUp?id=${encodeURIComponent(body.id)}`, body)
          : post<string>('/api/Patients/CreateWrapUp', body),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: wrapUpKey(encounterId, patientId) });
    },
  });
}
